import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Loader2 } from "lucide-react";

interface AISmartEntryProps {
  onSuccess: () => void;
  initialText?: string;
}

export const AISmartEntry = ({ onSuccess, initialText = "" }: AISmartEntryProps) => {
  const { toast } = useToast();
  const [input, setInput] = useState(initialText);
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState<any>(null);
  const [editing, setEditing] = useState(false);

  // Auto-parse when initialText is provided (from voice)
  useEffect(() => {
    const autoParse = async () => {
      if (initialText && initialText.trim() && !loading && !parsed) {
        setInput(initialText);
        setLoading(true);
        toast({
          title: "Parsing Entry...",
          description: "AI is analyzing your input",
        });
        
        try {
          const { data, error } = await supabase.functions.invoke('parse-smart-entry', {
            body: { text: initialText }
          });

          if (error) throw error;

          setParsed(data.parsed);
          setEditing(true);
          
          toast({
            title: "Entry Parsed",
            description: "Review and confirm the details below.",
          });
        } catch (error) {
          console.error('Parse error:', error);
          toast({
            title: "Parse Error",
            description: "Failed to parse entry. Please try again.",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      }
    };
    
    autoParse();
  }, [initialText]);

  const handleParse = async () => {
    if (!input.trim()) return;

    setLoading(true);
    toast({
      title: "Parsing Entry...",
      description: "AI is analyzing your input",
    });
    
    try {
      const { data, error } = await supabase.functions.invoke('parse-smart-entry', {
        body: { text: input }
      });

      if (error) throw error;

      setParsed(data.parsed);
      setEditing(true);
      
      toast({
        title: "Entry Parsed",
        description: "Review and confirm the details below.",
      });
    } catch (error) {
      console.error('Parse error:', error);
      toast({
        title: "Parse Error",
        description: "Failed to parse entry. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      let error;
      let insertedData: any = null;

      switch (parsed.type) {
        case 'lead':
          ({ data: insertedData, error } = await supabase.from('sales_leads').insert({
            user_id: user.id,
            company_name: parsed.company || parsed.client || 'Unknown',
            contact_name: parsed.title || '',
            estimated_value: parsed.value || parsed.amount || 0,
            notes: parsed.description || input,
            stage: 'lead',
          }).select().single());
          break;

        case 'task':
          ({ data: insertedData, error } = await supabase.from('tasks').insert({
            user_id: user.id,
            title: parsed.title || input,
            description: parsed.description || '',
            due_date: parsed.due_date || parsed.date || null,
            priority: parsed.priority || 'medium',
            status: 'todo',
          }).select().single());
          break;

        case 'invoice':
          const invoiceNumber = `INV-${Date.now()}`;
          const amount = parsed.amount || 0;
          ({ data: insertedData, error } = await supabase.from('invoices').insert({
            user_id: user.id,
            invoice_number: invoiceNumber,
            subtotal: amount,
            total: amount,
            due_date: parsed.due_date || parsed.date || null,
            status: 'draft',
          }).select().single());
          break;

        case 'income':
        case 'expense':
          ({ data: insertedData, error } = await supabase.from('finance_transactions').insert({
            user_id: user.id,
            type: parsed.type,
            amount: parsed.amount || 0,
            category: parsed.category || '',
            description: parsed.description || input,
            transaction_date: parsed.date || new Date().toISOString().split('T')[0],
          }).select().single());
          break;

        default:
          throw new Error('Unknown entry type');
      }

      if (error) throw error;

      // Save to capture history
      if (insertedData) {
        await supabase.from('capture_history').insert({
          user_id: user.id,
          entry_type: parsed.type,
          entry_id: insertedData.id,
          data: parsed,
        });
      }

      toast({
        title: "Success",
        description: `${parsed.type.charAt(0).toUpperCase() + parsed.type.slice(1)} created successfully!`,
      });
      
      onSuccess();
    } catch (error) {
      console.error('Confirm error:', error);
      toast({
        title: "Error",
        description: "Failed to create entry. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (editing && parsed) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-muted rounded-lg">
          <h3 className="font-semibold mb-2">Confirm Details</h3>
          <div className="space-y-2 text-sm">
            <div><strong>Type:</strong> {parsed.type}</div>
            {parsed.title && <div><strong>Title:</strong> {parsed.title}</div>}
            {parsed.company && <div><strong>Company:</strong> {parsed.company}</div>}
            {parsed.client && <div><strong>Client:</strong> {parsed.client}</div>}
            {parsed.amount && <div><strong>Amount:</strong> ₹{parsed.amount}</div>}
            {parsed.value && <div><strong>Value:</strong> ₹{parsed.value}</div>}
            {parsed.date && <div><strong>Date:</strong> {parsed.date}</div>}
            {parsed.due_date && <div><strong>Due Date:</strong> {parsed.due_date}</div>}
            {parsed.priority && <div><strong>Priority:</strong> {parsed.priority}</div>}
            {parsed.category && <div><strong>Category:</strong> {parsed.category}</div>}
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={() => { setEditing(false); setParsed(null); }} 
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={loading}
            className="flex-1"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="smart-input">Describe what you want to capture</Label>
        <Textarea
          id="smart-input"
          placeholder='e.g., "Create invoice for Caplin Labs ₹25,000 due next week" or "Add task to design landing page before Friday"'
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={4}
          className="mt-2"
        />
      </div>

      <Button 
        onClick={handleParse} 
        disabled={loading || !input.trim()}
        className="w-full"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Parsing...
          </>
        ) : (
          <>
            <Sparkles className="mr-2 h-4 w-4" />
            Parse with AI
          </>
        )}
      </Button>

      <div className="text-xs text-muted-foreground">
        <strong>Examples:</strong>
        <ul className="list-disc list-inside mt-1 space-y-1">
          <li>"Invoice for Tech Corp ₹50,000 due 15th"</li>
          <li>"Task: Follow up with client tomorrow"</li>
          <li>"Expense: Lunch ₹600 today"</li>
          <li>"Lead from Gensol worth ₹1L"</li>
        </ul>
      </div>
    </div>
  );
};